import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Kpi, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtUSDStrict, timeAgo, verdictLabel } from "@/lib/format";
import { toastError } from "@/lib/toastError";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, AlertTriangle, RefreshCw, CalendarClock, HandCoins, ChevronDown, ArrowRight,
  BadgeCheck, Ban,
} from "lucide-react";

const TONE = {
  "Compliant": { badge: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: BadgeCheck, bar: "bg-emerald-500" },
  "Questionable": { badge: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle, bar: "bg-amber-500" },
  "Non-Compliant": { badge: "bg-rose-50 text-rose-700 border-rose-200", icon: Ban, bar: "bg-rose-500" },
};

const Ratio = ({ label, value, limit, invert }) => {
  if (value === null || value === undefined) return null;
  const pass = invert ? value >= limit : value < limit;
  const pct = Math.min(100, (value / (limit * 2)) * 100);
  return (
    <div>
      <div className="flex justify-between text-[11px] mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className={`numeric font-semibold ${pass ? "text-emerald-600" : "text-rose-600"}`}>{value}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${pass ? "bg-emerald-500" : "bg-rose-500"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const HoldingRow = ({ h, onOpen }) => {
  const [open, setOpen] = useState(false);
  const tone = TONE[h.status] || TONE.Questionable;
  const Icon = tone.icon;
  return (
    <div className="rounded-2xl border border-border/60 overflow-hidden" data-testid={`shariah-row-${h.symbol}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-start p-4 hover:bg-muted/40 transition-colors">
        <div className="flex items-center gap-3">
          <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${tone.badge} border`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">{h.symbol}</span>
              <Badge variant="outline" className={`text-[10px] ${tone.badge}`} data-testid={`shariah-status-${h.symbol}`}>{verdictLabel(h.status)}</Badge>
              {h.live && <span className="text-[10px] text-muted-foreground">live filings</span>}
            </div>
            <div className="text-xs text-muted-foreground truncate">{h.name} · {h.sector}</div>
          </div>
          <div className="text-end">
            <div className="numeric text-sm font-semibold">{fmtUSDStrict(h.value_usd)}</div>
            <div className="text-[11px] text-muted-foreground numeric">{h.qty} units</div>
          </div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/60 pt-3" data-testid={`shariah-detail-${h.symbol}`}>
          <div className="grid sm:grid-cols-3 gap-3">
            <Ratio label="Interest-bearing debt (<30%)" value={h.debt_ratio} limit={30} />
            <Ratio label="Non-permissible income (<5%)" value={h.interest_income_pct} limit={5} />
            <Ratio label="Illiquid assets (>20%)" value={h.illiquid_ratio} limit={20} invert />
          </div>
          {h.reasons?.length > 0 && (
            <ul className="space-y-1">
              {h.reasons.map((r, i) => (
                <li key={`${r}-${i}`} className="text-xs text-rose-700 flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{r}</li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
            <span>Purification factor <span className="numeric font-semibold text-foreground">{h.purification_pct}%</span></span>
            <span>Dividends received <span className="numeric font-semibold text-foreground">{fmtUSDStrict(h.dividends_received_usd)}</span></span>
            <span>To purify <span className="numeric font-semibold text-foreground">{fmtUSDStrict(h.purification_owed_usd)}</span></span>
            <span>{h.screened_at ? `Screened ${timeAgo(h.screened_at)}` : "Not screened yet"}</span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onOpen(h.symbol)}
              data-testid={`shariah-open-${h.symbol}`}>Open {h.symbol} <ArrowRight className="h-3.5 w-3.5 ms-1" /></Button>
            {h.purification_owed_usd > 0 && (
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onOpen(null, "/zakat")}
                data-testid={`shariah-purify-${h.symbol}`}>Purify income</Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ShariahWatch() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/shariah/watch");
      setData(d);
    } catch (e) {
      toastError(e, "Could not load Shariah Watch");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rescan = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/shariah/rescan");
      setData(d);
      toast.success(d.changes.length
        ? `${d.changes.length} holding${d.changes.length > 1 ? "s" : ""} changed verdict`
        : `All ${d.screened} holdings re-screened — no changes`);
    } catch (e) {
      toastError(e, "Re-screen failed");
    } finally {
      setBusy(false);
    }
  };

  const open = (symbol, to) => navigate(to || `/stock/${symbol}`);
  const s = data?.summary;

  return (
    <AppShell>
      <div className="space-y-4 sm:space-y-5" data-testid="shariah-watch-page">
        <PageHeader title="Shariah Watch" subtitle="Every holding re-screened against AAOIFI standards every 30 days — you hear from us the moment a verdict changes."
          action={
            <Button onClick={rescan} disabled={busy} className="rounded-full" data-testid="shariah-rescan-button">
              <RefreshCw className={`h-4 w-4 me-1.5 ${busy ? "animate-spin" : ""}`} />Re-screen now
            </Button>
          } />

        {!data ? <EmptyState title="Loading your holdings…" /> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Kpi label="Shariah compliant holdings" value={`${s.compliant} / ${s.screened}`} icon={ShieldCheck} tint={0} accent testid="shariah-kpi-compliant" />
              <Kpi label="Shariah compliant by value" value={`${s.compliant_value_pct}%`} icon={BadgeCheck} tint={1} testid="shariah-kpi-value" />
              <Kpi label="Needs your review" value={s.needs_review} icon={AlertTriangle} tint={2} testid="shariah-kpi-review" />
              <Kpi label="Income to purify" value={fmtUSDStrict(s.purification_owed_usd)} icon={HandCoins} tint={3} testid="shariah-kpi-purify" />
            </div>

            <Card className="p-4 shadow-premium flex flex-wrap items-center gap-x-6 gap-y-2 text-xs" data-testid="shariah-cycle">
              <span className="flex items-center gap-1.5 font-semibold"><CalendarClock className="h-4 w-4 text-emerald-600" />Monthly cycle</span>
              <span className="text-muted-foreground">Last screened {s.last_screened ? timeAgo(s.last_screened) : "never"}</span>
              <span className="text-muted-foreground">Next due {s.next_due || "on your first screen"}</span>
              <span className="text-muted-foreground">{data.methodology}</span>
            </Card>

            <div className="grid gap-4 sm:gap-5 lg:grid-cols-3">
              <Card className="p-5 shadow-premium lg:col-span-2">
                <div className="text-sm font-semibold mb-3">Your holdings</div>
                {data.holdings.length === 0 ? (
                  <EmptyState title="Nothing to screen yet" subtitle="Buy a stock, ETF or sukuk and it joins the monthly screen automatically." />
                ) : (
                  <div className="space-y-2.5" data-testid="shariah-holdings-list">
                    {data.holdings.map((h) => <HoldingRow key={h.symbol} h={h} onOpen={open} />)}
                  </div>
                )}
              </Card>

              <Card className="p-5 shadow-premium" data-testid="shariah-changes-card">
                <div className="text-sm font-semibold mb-3">Verdict history</div>
                {data.changes.length === 0 ? (
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    No verdict has changed since you started. When one does, it appears here and we
                    send you a notification and an email explaining exactly which screen it failed.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {data.changes.map((c) => (
                      <div key={c.id} className="flex gap-2.5" data-testid={`shariah-change-${c.id}`}>
                        <div className={`h-7 w-7 rounded-lg shrink-0 flex items-center justify-center ${c.direction === "downgrade" ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>
                          {c.direction === "downgrade" ? <AlertTriangle className="h-3.5 w-3.5" /> : <BadgeCheck className="h-3.5 w-3.5" />}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold">{c.symbol}: {verdictLabel(c.was)} → {verdictLabel(c.now)}</div>
                          <div className="text-[11px] text-muted-foreground">{c.reasons?.[0] || c.name}</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(c.timestamp)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
